
import { CountryType } from "@/pages/app/courses/index.js";
import type { ApiResponse, CourseActionType } from "@repo/trpc";
import type { ChannelType, ChannelFilterType, MessagingChannelType } from "@repo/gcp-sql";
import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "../context/SnackbarContext.js";
import { getCurrentUser } from "./firebase.js";
import { clearReferralData } from "./referralHandler.js";
import { baseTrpcClient, getBaseAPIURL, queryClient, trpc } from "./trpc.js";

// Re-export CourseActionType for convenience
export type { CourseActionType };

export interface ErrorData {
  httpStatus?: number;
}

export interface WrappedQueryOptions {
  showErrorSnackbar?: boolean;
  refetchInterval?: number;
  enabled?: boolean;
  keepPreviousData?: boolean;
  staleTime?: number;
  gcTime?: number;
}

export interface WrappedMutationOptions {
  showErrorSnackbar?: boolean;
  onSuccess?: (...args: any[]) => void;
  onError?: (...args: any[]) => void;
}

/**
 * Extract error code and message from tRPC errors
 */
export const extractErrorInfo = (
  error: unknown,
): { errorCode: string; message: string } => {
  // tRPC error structure: error.data.code contains the error code, error.data.httpStatus contains HTTP status
  const errorCode =
    (error as unknown as { data: { code: string } })?.data?.code ||
    (
      error as unknown as { data: { httpStatus: number } }
    )?.data?.httpStatus?.toString() ||
    "UNKNOWN";
  const message =
    (error as unknown as { message: string })?.message || "An error occurred";

  return { errorCode, message };
};

/**
 * Wrapper for useQuery that automatically shows error snackbars
 */
export function useWrappedQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>(
  queryOptions: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  options?: WrappedQueryOptions,
): UseQueryResult<TData, TError> {
  const { showSnackbar } = useSnackbar();
  const result = useQuery({
    ...queryOptions,
    enabled: options?.enabled ?? queryOptions.enabled ?? true,
    refetchInterval: options?.refetchInterval ?? queryOptions.refetchInterval,
    staleTime: options?.staleTime ?? queryOptions.staleTime,
    gcTime: options?.gcTime ?? queryOptions.gcTime,
  });
  useEffect(() => {
    if (result.error) {
      const { errorCode, message } = extractErrorInfo(result.error);
      if (options?.showErrorSnackbar !== false && errorCode !== "UNKNOWN") {
        showSnackbar(message, "error");
      }
    }
  }, [result.error, options?.showErrorSnackbar, showSnackbar, options]);
  return result;
}

/**
 * Wrapper for useMutation that automatically shows error snackbars
 */
export function useWrappedMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  mutationOptions: UseMutationOptions<TData, TError, TVariables, TContext>,
  options?: WrappedMutationOptions,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { showSnackbar } = useSnackbar();
  return useMutation({
    ...mutationOptions,
    onSuccess: (data, variables, result, context) => {
      // Call original onSuccess if it exists
      if (mutationOptions.onSuccess) {
        mutationOptions.onSuccess(data, variables, result, context);
      }

      // Call options onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data, variables, result, context);
      }
    },
    onError: (error, variables, result, context) => {
      const { message } = extractErrorInfo(error);

      // Show snackbar by default (unless explicitly disabled)
      if (options?.showErrorSnackbar !== false) {
        showSnackbar(message, "error");
      }

      // Call original onError if it exists
      if (mutationOptions.onError) {
        mutationOptions.onError(error, variables, result, context);
      }

      // Call options onError if it exists
      if (options?.onError) {
        options.onError(error, variables, context);
      }
    },
  });
}

/**
 * Common user hooks - works for any authenticated user regardless of role
 */
export const useCommonCurrentUser = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) => {
  const firebaseUser = getCurrentUser();
  return useWrappedQuery(
    trpc.commonUser.getCurrentUser.queryOptions(undefined, {
      staleTime: 60 * 1000,
      retry: (failureCount: number, error: unknown) => {
        // Type assertion with safeguard
        if (!(error instanceof Error)) return failureCount < 1;
        const trpcError = error as unknown as { data: { httpStatus: number } };
        return trpcError.data?.httpStatus === 401 ? false : failureCount < 1;
      },
      enabled: (options?.enabled ?? true) && !!firebaseUser,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );
};

/**
 * User data hooks
 */
export const useUpdateCookiePreferences = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.users.updateCookiePreferences.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUserShort.queryKey(),
        });
      },
    }),
    options,
  );

export const useCurrentStudentUser = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) => {
  const firebaseUser = getCurrentUser();
  const { data: userShort } = useCurrentStudentUserShort();
  const isStudent = Boolean(userShort && userShort.data.userType === "student");
  return useWrappedQuery(
    trpc.users.getCurrentStudentUser.queryOptions(undefined, {
      staleTime: 60 * 1000,
      retry: (failureCount: number, error: unknown) => {
        // Type assertion with safeguard
        if (!(error instanceof Error)) return failureCount < 1;
        const trpcError = error as unknown as { data: { httpStatus: number } };
        return trpcError.data?.httpStatus === 401 ||
          trpcError.data?.httpStatus === 403
          ? false
          : failureCount < 1;
      },
      enabled: (options?.enabled ?? true) && !!firebaseUser && isStudent,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );
};

export const useCurrentStudentUserShort = (
  options?: {
    enabled?: boolean;
    onMissingCreate?: boolean;
  } & WrappedQueryOptions,
) => {
  const firebaseUser = getCurrentUser();
  const location =
    typeof window !== "undefined" ? window.location.pathname : undefined;
  const { data: commonUser } = useCommonCurrentUser();
  const shouldProceed = commonUser && (commonUser.data.userType === "student" || options?.onMissingCreate && commonUser.data.userType === 'unassigned');
  const isValidStudentPath = location ? location.startsWith("/app/") || location.startsWith("/auth/") : false;
  const onMissingCreate = options?.onMissingCreate
  return useWrappedQuery(
    trpc.users.getCurrentStudentUserShort.queryOptions(
      { onMissingCreate },
      {
        staleTime: 60 * 1000,
        retry: (failureCount: number, error: unknown) => {
          // Type assertion with safeguard
          if (!(error instanceof Error)) return failureCount < 1;
          const trpcError = error as unknown as {
            data: { httpStatus: number };
          };
          return trpcError.data?.httpStatus === 401 || trpcError.data?.httpStatus === 403 ? false : failureCount < 1;
        },
        enabled: (options?.enabled ?? true) && !!firebaseUser && isValidStudentPath && shouldProceed,
      },
    ),
    // TODO: Temp fix, basically other paths are somehow calling this function even when it's disabled. Might be linked to AuthProvider using refetch
    { ...options, showErrorSnackbar: false },
  );
};

/**
 * Hook to get the current student's organization
 */
export const useStudentOrganization = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) => {
  const { data: user } = useCommonCurrentUser();
  const enabled = Boolean((options?.enabled ?? true) && user && user.data && user.data.organizationId);
  return useWrappedQuery(
    trpc.commonUser.getMyOrganization.queryOptions(undefined, {
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 0,
      retry: (failureCount: number, error: unknown) => {
        if (!(error instanceof Error)) return failureCount < 1;
        const trpcError = error as unknown as { data: { httpStatus: number } };
        const errorCode = trpcError.data?.httpStatus;
        if (errorCode === 404 || errorCode === 403 || errorCode === 401) {
          return false;
        }
        return failureCount < 1;
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? false,
    },
  );
};

/**
 * Unified user profile update that can handle any type of profile update
 */
export const useUpdateStudentUserProfile = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.users.updateUser.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUser.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUserShort.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Apply referral code with UTM tracking
 */
export const useApplyReferral = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.users.applyReferral.mutationOptions({
      onSuccess: () => clearReferralData(),
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Delete the current user's account (requires authentication)
 */
export const useDeleteAccount = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.users.deleteAccount.mutationOptions(),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**

/**
* Search courses with pagination and filtering (unified system for both UK and US)
* Uses discriminated union for type-specific filters
*/
export const useSearchCourses = (
  params: {
    country: CountryType;
    degreeName?: string;
    universityIds?: string[];
    cities?: string[];
    courseIds?: string[];
    categories?: string[];
    sortBy?: "name" | "university-ranking";
    sortOrder?: "asc" | "desc";
    page: number;
    pageSize: number;
    onlyQualified?: boolean;
    enabled?: boolean;
    // Discriminated union for type-specific filters
    specificFilters?:
    | { type: 'uk_undergrad'; withIntegratedFoundation?: boolean; withIntegratedMasters?: boolean; withYearIndustry?: boolean; withStudyAbroad?: boolean }
    | { type: 'uk_foundation' }
    | { type: 'us_undergrad'; states?: string[]; satRange?: { min?: number; max?: number }; actRange?: { min?: number; max?: number }; tuitionRange?: { min?: number; max?: number }; acceptanceRateRange?: { min?: number; max?: number } };
  },
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.courses.searchCourses.queryOptions(params, {
      enabled: params.enabled ?? true,
    }),
    options,
  );

/**
 * Get search filters (degree types, universities, durations) with country support
 */
export const useSearchFilters = (
  params?: { showErrorSnackbar?: boolean },
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.courses.getSearchFilters.queryOptions(),
    {
      showErrorSnackbar: params?.showErrorSnackbar ?? true,
      ...options,
    },
  );

/**
 * Get a course by ID (unified system for both UK and US courses)
 */
export const useCourseDetails = (
  courseId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.courses.getCourse.queryOptions(
      { courseId: courseId ?? "" },
      {
        enabled: (options?.enabled ?? true) && !!courseId,
        staleTime: 30 * 1000, // 30 seconds
      },
    ),
    options,
  );

/**
 * Get user course application data (unified system for both UK and US courses)
 */
export const useGetUserCourseApplication = (
  courseId: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.courses.getUserCourseApplication.queryOptions(
      { courseId },
      {
        enabled: (options?.enabled ?? true) && !!courseId,
        staleTime: 30 * 1000, // 30 seconds
      },
    ),
    options,
  );

/**
 * Get all courses associated with the current user (unified system for both UK and US)
 */
export const useGetAllUserCourses = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.courses.getStudentCourses.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    { ...options, showErrorSnackbar: false },
  );

/**
 * Set the status for a course (unified system for both UK and US)
 */
export const useSetCourseStatus = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      ...trpc.courses.setCourseStatus.mutationOptions(),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.courses.getStudentCourses.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.courses.searchCourses.queryKey(),
        });
      },
    },
    options,
  );

/**
 * File Management Hooks
 */

/**
 * Get a presigned URL for uploading a file to GCS
 */
export const useGetFileUploadUrl = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.files.getFileUploadUrl.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.files.getUserFiles.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUser.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Get a presigned URL for uploading a file via magic link (unauthenticated)
 */
export const useGetMagicLinkFileUploadUrl = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.files.getMagicLinkFileUploadUrl.mutationOptions(),
    options,
  );

/**
 * Delete a file by ID
 */
export const useDeleteFile = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.files.deleteFile.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: trpc.files.getUserFiles.queryKey(),
        }),
    }),
    options,
  );

/**
 * Process an uploaded file after it's been uploaded to S3
 */
export const useProcessUploadedFile = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.users.processUploadedFile.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.files.getUserFiles.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUserShort.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUser.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Get all files for the current user
 */
export const useGetUserFiles = (options?: WrappedQueryOptions) =>
  useWrappedQuery(trpc.files.getUserFiles.queryOptions(), options);

/**
 * Get file details by ID
 */
export const useGetFileById = (fileId: string, options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.files.getFileById.queryOptions(
      { fileId },
      {
        enabled: !!fileId,
      },
    ),
    options,
  );

/**
 * Get a signed download URL for a file
 */
export const useGetFileDownloadUrl = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.files.getFileDownloadUrl.mutationOptions(),
    options,
  );

/**
 * Get chat history by session ID
 */
export const useGetChatHistory = (
  sessionId: string = "",
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.chat.getHistory.queryOptions(
      { sessionId },
      {
        enabled: !!sessionId && sessionId.trim().length > 0,
      },
    ),
    options,
  );

/**
 * Get user's chat sessions
 */
export const useGetUserChatSessions = (
  params: {
    chatType?: 'student_generic' | 'student_psychometric_helper';
  } = {},
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.chat.listUserSessions.queryOptions(params),
    options,
  );

/**
 * Update chat session title
 */
export const useUpdateSessionTitle = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.chat.updateSessionTitle.mutationOptions(),
    options,
  );

/**
 * Delete chat session
 */
export const useDeleteSession = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.chat.deleteSession.mutationOptions(),
    options,
  );

/**
 * Get course recommendations for the user (unified system for both UK and US)
 */
export const useCourseRecommendation = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.courses.getStudentCourseRecommendation.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUser.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Initialize a chat session with proper system message (gets existing or creates new)
 */
export const useInitializeChat = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.chat.initializeChat.mutationOptions(),
    options,
  );

/**
 * Create a completely new chat session (always creates fresh session)
 */
export const useCreateNewChat = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.chat.createNewChat.mutationOptions(),
    options,
  );

export const useCreateStreamedChatSender = (): ((chatParams: {
  sessionId?: string;
  message: string;
  chatType?: 'student_generic' | 'student_psychometric_helper';
  file?: {
    name: string;
    url: string;
    type: string;
  };
}) => Promise<AsyncGenerator<{ content: string }>>) =>
  useCallback(
    async (chatParams: {
      sessionId?: string;
      message: string;
      chatType?: 'student_generic' | 'student_psychometric_helper';
      file?: {
        name: string;
        url: string;
        type: string;
      };
    }): Promise<AsyncGenerator<{ content: string }>> => {
      let actualSessionId = chatParams.sessionId;

      // If no sessionId provided, initialize chat first
      if (!actualSessionId && chatParams.chatType) {
        const initResponse = await baseTrpcClient.chat.initializeChat.mutate({
          chatType: chatParams.chatType,
        });
        actualSessionId = initResponse.data.sessionId;
      }

      if (!actualSessionId) {
        throw new Error('No sessionId provided and no chatType for initialization');
      }

      return (await baseTrpcClient.chat.streamMessage.query({
        sessionId: actualSessionId,
        message: chatParams.message,
        file: chatParams.file,
      })) as AsyncGenerator<{ content: string }>;
    },
    [],
  );

/**
 * Get scholarship by ID
 */
export const useGetScholarship = (id?: string, options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.scholarships.getScholarship.queryOptions(
      { scholarshipId: id ?? "" },
      {
        enabled: !!id,
      },
    ),
    options,
  );
/**
 * Enhanced search scholarships with advanced filters
 */
export const useSearchScholarshipsEnhanced = (params: {
  countryCode: 'gb' | 'us';
  query?: string;
  nationalities?: string[]; // Country ISO codes
  fundingTypes?: ('full_tuition' | 'partial_tuition' | 'living_allowance' | 'one_time')[];
  courseCategories?: string[];
  providerType?: string;
  eligibilityType?: string;
  eligibleDegrees?: ('undergraduate' | 'masters')[];
  universityIds?: string[];
  sortBy?: 'name' | 'provider' | 'funding_amount' | 'deadline';
  sortOrder?: 'asc' | 'desc';
  page: number;
  pageSize: number;
}, options?: WrappedQueryOptions) => useWrappedQuery(
  trpc.scholarships.searchScholarshipsEnhanced.queryOptions(params),
  options
);

/**
 * Get enhanced scholarship filter options (including course categories, nationalities)
 */
export const useScholarshipFilterOptions = (options?: WrappedQueryOptions) => useWrappedQuery(
  trpc.scholarships.getEnhancedFilters.queryOptions(),
  options
);

/**
 * Get user's scholarship watchlist
 */
export const useScholarshipWatchlist = (params: {
  status?: 'watchlist' | 'applied' | 'rejected' | 'awarded';
  countryCode: 'gb' | 'us';
}, options?: WrappedQueryOptions & { enabled?: boolean }) => useWrappedQuery(
  trpc.scholarships.getWatchlist.queryOptions(params, {
    enabled: (options?.enabled ?? true)
  }),
  options
);

/**
 * Add scholarship to watchlist
 */
export const useAddToScholarshipWatchlist = (options?: WrappedMutationOptions) => useWrappedMutation({
  ...trpc.scholarships.addToWatchlist.mutationOptions(),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: trpc.scholarships.getWatchlist.queryKey() });
    queryClient.invalidateQueries({ queryKey: trpc.scholarships.searchScholarshipsEnhanced.queryKey() });
  },
}, options);

/**
 * Remove scholarship from watchlist
 */
export const useRemoveFromScholarshipWatchlist = (options?: WrappedMutationOptions) => useWrappedMutation({
  ...trpc.scholarships.removeFromWatchlist.mutationOptions(),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: trpc.scholarships.getWatchlist.queryKey() });
    queryClient.invalidateQueries({ queryKey: trpc.scholarships.searchScholarshipsEnhanced.queryKey() });
  },
}, options);

/**
 * Update scholarship watchlist status (watchlist -> applied -> outcome)
 */
export const useUpdateScholarshipWatchlistStatus = (options?: WrappedMutationOptions) => useWrappedMutation({
  ...trpc.scholarships.updateWatchlistStatus.mutationOptions(),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: trpc.scholarships.getWatchlist.queryKey() });
  },
}, options);

/**
 * Get personalized scholarship recommendations
 */
export const useScholarshipRecommendations = (params?: {
  limit?: number;
}, options?: WrappedQueryOptions) => useWrappedQuery(
  trpc.scholarships.getPersonalizedRecommendations.queryOptions(params || {}),
  options
);

/**
 * Course application hooks (unified system for both UK and US courses)
 */
export const useCourseApplication = (
  courseId?: string,
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    {
      ...trpc.courses.getUserCourseApplication.queryOptions(
        { courseId: courseId ?? "" },
        {
          enabled: !!courseId,
          staleTime: 60 * 1000,
        },
      ),
    },
    options,
  );

/**
 * Generate a personal statement outline based on the user's profile (unified system for both UK and US)
 */
export const useGeneratePersonalStatementOutline = (
  options?: WrappedMutationOptions,
) =>
  useWrappedMutation(
    {
      ...trpc.courses.generatePersonalStatementOutline.mutationOptions(),
      onSuccess: (_, variables) => {
        // Invalidate relevant queries when outline is generated
        queryClient.invalidateQueries({
          queryKey: trpc.courses.getUserCourseApplication.queryKey({
            courseId: variables.courseId,
          }),
        });
      },
    },
    options,
  );

/**
 * Update student course application (outline and/or essay) (unified system for both UK and US)
 */
export const useUpdateStudentCourseApplication = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      ...trpc.courses.updateStudentCourseApplication.mutationOptions(),
      onSuccess: (_, variables) => {
        // Invalidate relevant queries when personal statement is updated
        queryClient.invalidateQueries({
          queryKey: trpc.courses.getUserCourseApplication.queryKey({
            courseId: variables.courseId,
          }),
        });
      },
    },
    options,
  );

/**
 * Generate feedback for personal statement (unified system for both UK and US)
 */
export const useGenerateFeedbackForPersonalStatement = (
  options?: WrappedMutationOptions,
) =>
  useWrappedMutation(
    {
      ...trpc.courses.generateFeedbackForPersonalStatement.mutationOptions(),
      onSuccess: (_, variables) => {
        // Invalidate relevant queries when feedback is generated
        queryClient.invalidateQueries({
          queryKey: trpc.courses.getUserCourseApplication.queryKey({
            courseId: variables.courseId,
          }),
        });
      },
    },
    options,
  );

/**
 * Set UCAS status for a course (unified system)
 */
export const useSetUcasStatus = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      ...trpc.courses.setUcasStatus.mutationOptions(),
      onSuccess: () => {
        // Invalidate relevant queries when UCAS status is updated
        queryClient.invalidateQueries({
          queryKey: trpc.courses.getStudentCourses.queryKey(),
        });
      },
    },
    options,
  );

/**
 * Validate course application (unified system for UK UCAS, US Early Decision, etc.)
 * Backend determines validation rules based on course metadata
 */
export const useValidateCourseApplication = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      ...trpc.courses.validateCourseApplication.mutationOptions(),
      // Don't invalidate queries for validation-only calls
    },
    options,
  );

/**
 * Get available subjects by curriculum for manual grade entry
 */
export const useGetAvailableSubjects = (options?: WrappedQueryOptions) =>
  useWrappedQuery(trpc.users.getAvailableSubjects.queryOptions(), options);

/**
 * Save draft of essay to history (unified system for both UK and US)
 */
export const useSaveDraft = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      ...trpc.courses.saveDraft.mutationOptions(),
      onSuccess: (_, variables) => {
        // Invalidate relevant queries when draft is saved
        queryClient.invalidateQueries({
          queryKey: trpc.courses.getUserCourseApplication.queryKey({
            courseId: variables.courseId,
          }),
        });
      },
    },
    options,
  );

/**
 * Get essay history by historyId and versionKey
 */
export const useGetEssayHistory = (
  historyId?: string,
  versionKey?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.courses.getEssayHistory.queryOptions(
      { historyId: historyId ?? "", versionKey: versionKey ?? "" },
      {
        enabled:
          (options?.enabled ?? true) &&
          !!(historyId && versionKey && historyId.trim() && versionKey.trim()),
      },
    ),
    options,
  );

/**
 * Tutorial hooks
 */

/**
 * Get tutorial video by index
 */
export const useGetTutorialVideo = (
  videoId: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.tutorial.getTutorialVideo.queryOptions(
      { videoId },
      {
        enabled: (options?.enabled ?? true) && !!videoId,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    ),
    options,
  );

/**
 * Referrals hooks
 */
export const useGetReferralStats = (options?: WrappedQueryOptions) =>
  useWrappedQuery(trpc.referrals.getReferralStats.queryOptions(), options);

/**
 * Session Management Functions
 */

/**
 * Create a session cookie by sending ID token to the API
 */
export const createSessionCookie = async (idToken: string): Promise<void> => {
  try {
    const response = await fetch(`${getBaseAPIURL()}/auth/session-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies in the request
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error?.message || "Failed to create session cookie",
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating session cookie:", error);
    throw error;
  }
};

/**
 * Clear the session cookie
 */
export const clearSessionCookie = async (): Promise<void> => {
  try {
    const response = await fetch(`${getBaseAPIURL()}/auth/session-logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies in the request
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error?.message || "Failed to clear session cookie",
      );
    }

    return;
  } catch (error) {
    console.error("Error clearing session cookie:", error);
    throw error;
  }
};

// ============= STAFF API HOOKS =============

/**
 * Get current staff user
 */
export const useCurrentStaffUser = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) => {
  const firebaseUser = getCurrentUser();
  return useWrappedQuery(
    trpc.staffUser.getCurrentStaffUser.queryOptions(undefined, {
      staleTime: 60 * 1000,
      retry: (failureCount: number, error: unknown) => {
        // Type assertion with safeguard
        if (!(error instanceof Error)) return failureCount < 1;
        const trpcError = error as unknown as { data: { httpStatus: number } };
        return trpcError.data?.httpStatus === 401 || trpcError.data?.httpStatus === 403 ? false : failureCount < 1;
      },
      enabled: (options?.enabled ?? true) && !!firebaseUser,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );
};

/**
 * Create staff profile
 */
export const useCreateStaffProfile = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffUser.createOrUpdateStaffProfile.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: trpc.staffUser.getCurrentStaffUser.queryKey(),
        }),
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update staff user information
 */
export const useUpdateStaffUser = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffUser.updateUser.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.staffUser.getCurrentStaffUser.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.commonUser.getCurrentUser.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update staff profile
 */
export const useUpdateStaffProfile = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffUser.updateStaffProfile.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: trpc.staffUser.getCurrentStaffUser.queryKey(),
        }),
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );


/**
 * Switch to staff, dry run and real both uses this
 */
export const useSwitchToStaff = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffUser.switchToStaff.mutationOptions({
      onSuccess: () => {
        // Invalidate all user queries so the layout can detect the new user state
        queryClient.invalidateQueries({
          queryKey: trpc.commonUser.getCurrentUser.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.staffUser.getCurrentStaffUser.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUser.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUserShort.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    }
  );

// ============= MAGIC LINK HOOKS =============

/**
 * Hook to validate magic link token
 */
export const useMagicLinkValidation = (
  token: string | null,
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.magicLinks.validateMagicLink.queryOptions(
      { token: token || "" },
      {
        enabled: !!token,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
      },
    ),
    options,
  );

/**
 * Hook to use magic link during signup
 */
export const useMagicLinkUsage = (options?: WrappedMutationOptions) =>
  useWrappedMutation(trpc.magicLinks.useMagicLink.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.staffUser.getCurrentStaffUser.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.commonUser.getCurrentUser.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.users.getCurrentStudentUser.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.users.getCurrentStudentUserShort.queryKey(),
      });
    }
  }), options);

/**
 * Hook to generate magic links for bulk invitations
 */
export const useGenerateMagicLinks = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinks.generateMagicLinks.mutationOptions(),
    options,
  );

/**
 * Hook to generate domain-based magic link
 */
export const useGenerateDomainMagicLink = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinks.generateDomainMagicLink.mutationOptions(),
    options,
  );

/**
 * Hook to get invitation statistics
 */
export const useInvitationStats = (options?: WrappedQueryOptions) =>
  useWrappedQuery(trpc.magicLinks.getInvitationStats.queryOptions(), options);

/**
 * Hook to get invitation history
 */
export const useInvitationHistory = (
  params: {
    limit?: number;
    offset?: number;
    status?: "pending" | "used" | "expired" | "revoked";
  },
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.magicLinks.getInvitationHistory.queryOptions(params),
    options,
  );

/**
 * Hook to revoke invitations
 */
export const useRevokeInvitations = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinks.revokeInvitations.mutationOptions(),
    options,
  );

/**
 * Hook to resend invitations
 */
export const useResendInvitations = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinks.resendInvitations.mutationOptions(),
    options,
  );


// ============= CONSOLIDATED INVITATION HOOKS =============

/**
 * Hook to send email invitations (handles both student and staff)
 */
export const useSendEmailInvitations = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffInvitations.sendEmailInvitations.mutationOptions(),
    options,
  );

/**
 * Hook to generate domain invitation link (handles both student and staff)
 */
export const useGenerateDomainLink = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffInvitations.generateDomainLink.mutationOptions(),
    options,
  );

// ============= COUNSELOR DASHBOARD HOOKS =============

/**
 * Get counselor student overview with optimized caching
 * Fast loading (<2 seconds) with 15-minute stale time
 */
export const useCounselorStudentOverview = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffDashboard.getStudentOverview.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 15 * 60 * 1000, // 15 minutes as specified in requirements
    }),
    options,
  );

/**
 * Get detailed student information for counselor dashboard
 * Comprehensive data with 15-minute stale time
 */
export const useStudentDashboardDetails = (
  studentId: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffDashboard.getStudentDetails.queryOptions(
      { studentId },
      {
        enabled: (options?.enabled ?? true) && !!studentId,
        staleTime: 15 * 60 * 1000, // 15 minutes as specified in requirements
      },
    ),
    options,
  );

/**
 * Get aggregated metrics for counselor dashboard
 * Summary statistics with 15-minute stale time
 */
export const useCounselorDashboardMetrics = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffDashboard.getStudentMetrics.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 15 * 60 * 1000, // 15 minutes as specified in requirements
    }),
    options,
  );

/**
 * Get student essay by ID for counselor viewing
 * Supports UCAS statements, course essays, and US college essays
 */
export const useStudentEssay = (
  studentId: string,
  courseId: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffDashboard.getStudentEssay.queryOptions(
      { studentId, courseId },
      {
        enabled: (options?.enabled ?? true) && !!(studentId && courseId),
        staleTime: 60 * 60 * 1000, // 60 minutes - essays change less frequently
      },
    ),
    options,
  );

// ============= STAFF EMAIL HOOKS =============

/**
 * Validate email domain for staff accounts
 * Checks if the email domain is allowed for staff registration
 */
export const useValidateStaffEmail = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffEmail.isEmailValidForStaff.mutationOptions(),
    options,
  );

/**
 * Send essay reminder email to student
 * Reminds student to complete their personal statement/essay for university applications
 */
export const useRemindStudentStartPersonalStatement = (
  options?: WrappedMutationOptions,
) =>
  useWrappedMutation(
    trpc.staffEmail.remindStudentStartPersonalStatement.mutationOptions(),
    options,
  );

/**
 * Send profile completion reminder email to student
 * Reminds student to complete their profile at https://app.getzube.com/app/profile
 */
export const useRemindStudentCompleteProfile = (
  options?: WrappedMutationOptions,
) =>
  useWrappedMutation(
    trpc.staffEmail.remindStudentCompleteProfile.mutationOptions(),
    options,
  );

// ============= ORGANIZATION HOOKS =============

/**
 * Hook to get the current user's organization
 */
export const useMyOrganization = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) => {
  const { data: user } = useCommonCurrentUser();
  const enabled = Boolean((options?.enabled ?? true) && user && user.data && user.data.organizationId);
  return useWrappedQuery(
    trpc.staffOrganization.getMyOrganization.queryOptions(undefined, {
      enabled,
      staleTime: (query) => query.state.status === 'error' ? 0 : 5 * 60 * 1000, // 5 minutes for success, 0 for errors
      gcTime: 0,
      retry: (failureCount: number, error: unknown) => {
        // Type assertion with safeguard
        if (!(error instanceof Error)) return failureCount < 1;
        const trpcError = error as unknown as { data: { httpStatus: number } };
        const errorCode = trpcError.data?.httpStatus;
        if (errorCode === 404 || errorCode === 403 || errorCode === 401) {
          return false;
        }
        return failureCount < 1;
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );
}

/**
 * Hook to join an organization using a magic link invitation token
 */
export const useJoinOrganizationWithInvitation = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffOrganization.joinOrganizationWithInvitation.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.staffOrganization.getMyOrganization.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.staffOrganization.getMyOrganizationHierarchy.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.commonUser.getCurrentUser.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.staffUser.getCurrentStaffUser.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Hook to upsert (create or update) organization
 */
export const useUpsertOrganization = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffOrganization.upsertOrganization.mutationOptions({
      onSuccess: () => {
        // Invalidate organization queries when organization is upserted
        queryClient.invalidateQueries({
          queryKey: trpc.staffOrganization.getMyOrganization.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Hook to update school settings (year levels and curriculum)
 */
export const useUpdateSchoolSettings = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffOrganization.updateSchoolSettings.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.staffOrganization.getMyOrganization.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Hook to update chain settings
 */
export const useUpdateChainSettings = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.staffOrganization.updateChainSettings.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.staffOrganization.getMyOrganization.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Hook to get chain schools by parent chain ID
 */
export const useGetChainSchools = (
  parentId: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffOrganization.getChainSchools.queryOptions(
      { parentId },
      { enabled: (options?.enabled ?? true) && !!parentId },
    ),
    options,
  );

/**
 * Hook to get organization hierarchy
 */
export const useGetMyOrganizationHierarchy = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffOrganization.getMyOrganizationHierarchy.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    options,
  );

/**
 * Hook to validate chain structure
 */
export const useValidateChainStructure = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      mutationFn: async (variables: {
        organizationId: string;
        parentChainId: string;
      }) =>
        await baseTrpcClient.staffOrganization.validateChainStructure.query(
          variables,
        ),
    },
    options,
  );

// ============= STAFF MANAGEMENT HOOKS =============

export const useStaffDirectory = (
  filters: {
    role?: "admin" | "counsellor" | "teacher";
    jobTitle?: string;
    search?: string;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffManagement.getStaffDirectory.queryOptions(filters, {
      enabled: options?.enabled ?? true,
    }),
    options,
  );

export const useGetStaffMember = (
  staffId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffManagement.getStaffMember.queryOptions(
      { staffId: staffId ?? "" },
      {
        enabled: (options?.enabled ?? true) && !!staffId,
      },
    ),
    options,
  );
// ============= CALENDAR HOOKS =============

/**
 * Generate calendar events
 */
export const useGenerateCalendarEvents = (
  options?: WrappedMutationOptions & { onSuccess?: (data: ApiResponse<{ message: string; eventsCreated: number }>) => void },
) =>
  useWrappedMutation(
    trpc.userCalendar.generateEvents.mutationOptions({
      onSuccess: options?.onSuccess,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get calendar connection status
 */
export const useCalendarConnectionStatus = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.userCalendar.getConnectionStatus.queryOptions(),
    options,
  );

/**
 * Get OAuth URL for calendar provider
 */
export const useGetCalendarOAuthUrl = (options?: WrappedMutationOptions) =>
  useWrappedMutation(trpc.userCalendar.getOAuthUrl.mutationOptions(), options);

// Note: connectCalendar is handled server-side via OAuth callback endpoints

/**
 * Select/change calendar
 */
export const useSelectCalendar = (
  options?: WrappedMutationOptions & { onSuccess?: () => void },
) =>
  useWrappedMutation(
    trpc.userCalendar.selectCalendar.mutationOptions({
      onSuccess: options?.onSuccess,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Disconnect calendar
 */
export const useDisconnectCalendar = (
  options?: WrappedMutationOptions & { onSuccess?: () => void },
) =>
  useWrappedMutation(
    trpc.userCalendar.disconnectCalendar.mutationOptions({
      onSuccess: options?.onSuccess,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get calendar events
 */
export const useCalendarEvents = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.userCalendar.getEvents.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    options,
  );

/**
 * Get available calendars for the user
 */
export const useAvailableCalendars = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.userCalendar.getAvailableCalendars.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    options,
  );

/**
 * Update calendar settings
 */
export const useUpdateCalendarSettings = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.userCalendar.updateCalendarSettings.mutationOptions(),
    options,
  );

/**
 * Migrate local events to external calendar
 */
export const useMigrateToExternal = (
  options?: WrappedMutationOptions & { onSuccess?: (data: ApiResponse<{ migrated: number; deleted: number }>) => void },
) =>
  useWrappedMutation(
    trpc.userCalendar.migrateToExternal.mutationOptions({
      onSuccess: (data) => {
        // Invalidate calendar-related queries to refresh UI
        queryClient.invalidateQueries({
          queryKey: trpc.userCalendar.getConnectionStatus.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.userCalendar.getEvents.queryKey(),
        });
        options?.onSuccess?.(data);
      },
    }),
    options,
  );

// ============= CALENDAR OAUTH FUNCTIONS =============

/**
 * Get OAuth URL for calendar provider (non-hook function)
 */
export const getOAuthUrl = async (provider: "google" | "microsoft") => {
  try {
    const response = await baseTrpcClient.userCalendar.getOAuthUrl.mutate({
      provider,
    });
    return response;
  } catch (error) {
    console.error("Failed to get OAuth URL:", error);
    throw error;
  }
};

/**
 * Save psychometric assessment scores
 */
export const useSavePsychometricScore = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      ...trpc.users.savePsychometricScore.mutationOptions(),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.users.getCurrentStudentUser.queryKey(),
        });
      },
    },
    options,
  );

/**
 * Get psychometric assessment results
 */
export const usePsychometricResults = (options?: WrappedQueryOptions) =>
  useWrappedQuery(trpc.users.getPsychometricResults.queryOptions(), options);

// ============= ZUBE AGENTS INTEGRATIONS HOOKS =============

/**
 * Get HubSpot account information (connection status, hub ID, scopes)
 */
export const useHubSpotAccountInfo = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.hubspot.getAccountInfo.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    options
  );

/**
 * List available HubSpot CRM objects (contacts, companies, deals, custom objects)
 */
export const useHubSpotListObjects = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.hubspot.listObjects.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000, // 5 minutes - objects don't change frequently
    }),
    options
  );

/**
 * Get schema for a specific HubSpot object type
 */
export const useHubSpotObjectSchema = (
  objectType: string,
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.hubspot.getObjectSchema.queryOptions(
      { objectType },
      {
        enabled: (options?.enabled ?? true) && !!objectType,
        staleTime: 10 * 60 * 1000, // 10 minutes
      }
    ),
    options
  );

/**
 * Get a sample record from HubSpot object for preview
 */
export const useHubSpotSampleRecord = (
  params: { objectType: string; properties?: string[] },
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.hubspot.getSampleRecord.queryOptions(params, {
      enabled: (options?.enabled ?? true) && !!params.objectType,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
    options
  );

/**
 * Disconnect HubSpot integration
 */
export const useHubSpotDisconnect = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.hubspot.disconnect.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.hubspot.getAccountInfo.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Get field mappings for HubSpot objects
 */
export const useHubSpotFieldMappings = (
  params?: { objectType?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.hubspot.getFieldMappings.queryOptions(params || {}, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
    options
  );

/**
 * Save field mapping configuration for a HubSpot object
 */
export const useHubSpotSaveFieldMapping = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.hubspot.saveFieldMapping.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.hubspot.getFieldMappings.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Remove field mapping configuration for a HubSpot object
 */
export const useHubSpotRemoveFieldMapping = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.hubspot.removeFieldMapping.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.hubspot.getFieldMappings.queryKey(),
        });
      },
    }),
    options
  );

// ============= WHATSAPP INTEGRATION HOOKS =============

/**
 * List all connected WhatsApp accounts for the user
 */
export const useWhatsAppAccounts = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.whatsapp.listAccounts.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    options
  );

/**
 * Disconnect a WhatsApp account
 */
export const useDisconnectWhatsAppAccount = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.whatsapp.disconnectAccount.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.whatsapp.listAccounts.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Update channel permissions for a WhatsApp account
 */
export const useUpdateWhatsAppChannelPermissions = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.whatsapp.updateChannelPermissions.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.whatsapp.listAccounts.queryKey(),
        });
      },
    }),
    options
  );

// ============= WHATSAPP API INTEGRATION HOOKS =============

/**
 * List all connected WhatsApp API accounts for the user
 * (Cloud API / Tech Provider flow - separate from WhatsApp Business App)
 */
export const useWhatsAppApiAccounts = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.whatsappApi.listAccounts.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    options
  );

/**
 * Disconnect a WhatsApp API account
 */
export const useDisconnectWhatsAppApiAccount = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.whatsappApi.disconnectAccount.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.whatsappApi.listAccounts.queryKey(),
        });
      },
    }),
    options
  );

// ============= WHATSAPP TEMPLATES HOOKS =============

/**
 * List all WhatsApp templates for a phone number
 */
export const useWhatsAppTemplates = (
  phoneNumberId: string,
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.whatsappTemplates.list.queryOptions(
      { phoneNumberId },
      {
        enabled: (options?.enabled ?? true) && !!phoneNumberId,
        staleTime: 60 * 1000, // 1 minute
      }
    ),
    options
  );

/**
 * Get a specific WhatsApp template
 */
export const useWhatsAppTemplate = (
  params: { phoneNumberId: string; templateKey: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.whatsappTemplates.get.queryOptions(params, {
      enabled: (options?.enabled ?? true) && !!(params.phoneNumberId && params.templateKey),
      staleTime: 60 * 1000, // 1 minute
    }),
    options
  );

/**
 * Create a new WhatsApp template (admin only)
 */
export const useCreateWhatsAppTemplate = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.whatsappTemplates.create.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.whatsappTemplates.list.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Update a WhatsApp template (admin only)
 */
export const useUpdateWhatsAppTemplate = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.whatsappTemplates.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.whatsappTemplates.list.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.whatsappTemplates.get.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Delete a WhatsApp template (admin only)
 */
export const useDeleteWhatsAppTemplate = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.whatsappTemplates.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.whatsappTemplates.list.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Sync WhatsApp templates from Meta Graph API
 */
export const useSyncWhatsAppTemplates = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.whatsappTemplates.syncTemplates.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.whatsappTemplates.list.queryKey(),
        });
      },
    }),
    options
  );

// ============= STAFF CALENDAR HOOKS =============

/**
 * Get contact preferences for automated outreach
 */
export const useContactPreferences = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.user.getContactPreferences.queryOptions(undefined, {
      staleTime: 60 * 1000, // 1 minute
    }),
    options
  );

/**
 * Update contact preferences for automated outreach
 */
export const useUpdateContactPreferences = (
  options?: {
    onSuccess?: () => void;
    showSuccessSnackbar?: boolean;
  } & WrappedMutationOptions,
) =>
  useWrappedMutation(
    trpc.zubeAgents.user.updateContactPreferences.mutationOptions({
      onSuccess: () => {
        // Invalidate contact preferences query
        queryClient.invalidateQueries({
          queryKey: [['zubeAgents', 'user', 'getContactPreferences']],
        });
        options?.onSuccess?.();
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get counselor's managed students
 */
export const useStaffManagedStudents = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.staffCalendar.getManagedStudents.queryOptions(undefined, {
      staleTime: 60 * 1000,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get calendar events for staff
 */
export const useStaffCalendarEvents = (
  params: {
    startDate: Date;
    endDate: Date;
    includeStudentEvents?: boolean;
    studentIds?: string[];
  },
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffCalendar.getCalendarEvents.queryOptions(params, {
      enabled: options?.enabled ?? true,
      staleTime: 30 * 1000,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get calendar generation settings for staff
 */
export const useStaffCalendarSettings = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.staffCalendar.getCalendarSettings.queryOptions(undefined, {
      staleTime: 60 * 1000,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

// ============= SHORT COURSES HOOKS =============

/**
 * Get all short courses for discovery page (cached)
 */
export const useShortCourses = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.shortCourses.getShortCourses.queryOptions(undefined, {
      staleTime: 5 * 60 * 1000, // 5 minutes client cache
    }),
    options
  );

/**
 * Get individual short course by ID (from cache)
 */
export const useShortCourse = (
  courseId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.shortCourses.getShortCourse.queryOptions(
      { courseId: courseId ?? "" },
      {
        enabled: (options?.enabled ?? true) && !!courseId,
        staleTime: 5 * 60 * 1000, // 5 minutes client cache
      }
    ),
    options
  );



/**
 * Update calendar event
 */
export const useStaffUpdateEvent = (
  options?: {
    onSuccess?: () => void;
    showSuccessSnackbar?: boolean;
  } & WrappedMutationOptions,
) =>
  useWrappedMutation(
    trpc.staffCalendar.updateEvent.mutationOptions({
      onSuccess: options?.onSuccess,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Delete calendar event
 */
export const useStaffDeleteEvent = (
  options?: {
    onSuccess?: () => void;
    showSuccessSnackbar?: boolean;
  } & WrappedMutationOptions,
) =>
  useWrappedMutation(
    trpc.staffCalendar.deleteEvent.mutationOptions({
      onSuccess: options?.onSuccess,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update calendar settings
 */
export const useStaffUpdateCalendarSettings = (
  options?: {
    onSuccess?: () => void;
    showSuccessSnackbar?: boolean;
  } & WrappedMutationOptions,
) =>
  useWrappedMutation(
    trpc.staffCalendar.updateCalendarSettings.mutationOptions({
      onSuccess: options?.onSuccess,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Generate deadline events
 */
// Unified generate events hook (single or bulk via studentIds)
export const useStaffGenerateDeadlineEvents = (
  options?: WrappedMutationOptions,
) =>
  useWrappedMutation(
    trpc.staffCalendar.generateDeadlineEvents.mutationOptions(),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get enhanced student overview with chain data and filtering support
 * Supports year level filtering and chain visibility
 */
export const useEnhancedStudentOverview = (
  includeChainData: boolean = false,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffDashboard.getEnhancedStudentOverview.queryOptions(
      { includeChainData },
      {
        enabled: options?.enabled ?? true,
        staleTime: 15 * 60 * 1000, // 15 minutes
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get students filtered by year level
 * Returns all students in a specific year level for the organization
 */
export const useStudentsByYearLevel = (
  yearLevel: number,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.staffDashboard.getStudentsByYearLevel.queryOptions(
      { yearLevel },
      {
        enabled:
          (options?.enabled ?? true) && yearLevel >= 1 && yearLevel <= 13,
        staleTime: 15 * 60 * 1000, // 15 minutes
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update year level for a single student
 * Allows admin/counsellor staff to assign year levels and class keys to students
 */
export const useUpdateStudentYearLevel = (options?: WrappedMutationOptions) =>
  useWrappedMutation(trpc.students.updateStudentYearLevel.mutationOptions(), {
    showErrorSnackbar: options?.showErrorSnackbar ?? true,
  });

/**
 * Bulk update year levels for multiple students
 * Allows admin/counsellor staff to assign the same year level to multiple students at once
 */
export const useBulkUpdateYearLevel = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.students.bulkUpdateYearLevel.mutationOptions(),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    }
  );

// ============= MAGIC LINKS DOCUMENTS HOOKS =============

/**
 * Validate magic link token (public - no auth required)
 */
export const useMagicLinkDocumentValidation = (token: string | null, options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.magicLinksDocuments.validateToken.queryOptions(
      { token: token || '' },
      {
        enabled: !!token,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
        refetchOnWindowFocus: false,
      }
    ),
    options
  );

/**
 * Submit document via magic link (public - no auth required)
 */
export const useSubmitMagicLinkDocument = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinksDocuments.submitDocument.mutationOptions(),
    options
  );

/**
 * Create reference letter request (protected)
 */
export const useCreateReferenceLetterRequest = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinksDocuments.createReferenceLetterRequest.mutationOptions(),
    options
  );

/**
 * Link an existing reference letter request to another course
 */
export const useLinkRequestToCourse = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinksDocuments.linkRequestToCourse.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.magicLinksDocuments.getMyRequests.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.magicLinksDocuments.getReceivedRequests.queryKey(),
        });
      },
    }),
    options
  );

export const useGetStudentTeachersUniversitiesWithDeadlines = (
  studentId: string,
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.magicLinksDocuments.getStudentTeachersUniversitiesWithDeadlines.queryOptions({
      studentId,
    }, {
      enabled: (options?.enabled ?? true) && !!studentId,
    }),
    options
  );

/**
 * Get my document requests (protected)
 */
export const useMyDocumentRequests = (
  params: {
    studentId?: string;
    status?: 'pending' | 'submitted' | 'approved' | 'expired' | 'cancelled';
    type?: 'reference_letter';
    limit?: number;
    offset?: number;
  } = {},
  options?: WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.magicLinksDocuments.getMyRequests.queryOptions({
      studentId: params.studentId,
      status: params.status,
      type: params.type,
      limit: params.limit || 20,
      offset: params.offset || 0,
    }),
    options
  );

export const useReceivedDocumentRequests = (
  params: {
    studentId?: string;
    status?: 'pending' | 'submitted' | 'approved' | 'expired' | 'cancelled';
    type?: 'reference_letter';
    limit?: number;
    offset?: number;
  } = {},
  options?: WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.magicLinksDocuments.getReceivedRequests.queryOptions({
      studentId: params.studentId,
      status: params.status,
      type: params.type,
      limit: params.limit || 20,
      offset: params.offset || 0,
    }),
    options
  );

/**
 * Send reminder for document request
 */
export const useSendDocumentReminder = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinksDocuments.sendReminder.mutationOptions(),
    options
  );

/**
 * Cancel document request
 */
export const useCancelDocumentRequest = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinksDocuments.cancelRequest.mutationOptions(),
    options
  );

/**
 * Send bulk reminder emails
 */
export const useSendBulkDocumentReminders = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinksDocuments.sendBulkReminders.mutationOptions(),
    options
  );

/**
 * Cancel multiple document requests
 */
export const useCancelBulkDocumentRequests = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinksDocuments.cancelBulkRequests.mutationOptions(),
    options
  );



// ============================================
// School Scholarships API Functions
// ============================================

/**
 * Get form options for scholarship creation (countries, provider types, etc.)
 */
export const useGetScholarshipFormOptions = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.schoolCustomScholarships.getFormOptions.queryOptions(),
    options,
  );

/**
 * Get school-specific scholarships (created by school staff)
 */
export const useGetSchoolScholarships = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.schoolCustomScholarships.getSchoolScholarships.queryOptions(),
    options,
  );

/**
 * Create a new school scholarship
 */
export const useCreateSchoolScholarship = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      ...trpc.schoolCustomScholarships.createSchoolScholarship.mutationOptions(),
      onSuccess: (_data, _variables, _context) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: trpc.schoolCustomScholarships.getSchoolScholarships.queryKey() });
        queryClient.invalidateQueries({ queryKey: trpc.scholarships.searchScholarshipsEnhanced.queryKey() });
      }
    },
    options,
  );

/**
 * Update an existing school scholarship
 */
export const useUpdateSchoolScholarship = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      ...trpc.schoolCustomScholarships.updateSchoolScholarship.mutationOptions(),
      onSuccess: (_data, _variables, _context) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: trpc.schoolCustomScholarships.getSchoolScholarships.queryKey() });
        queryClient.invalidateQueries({ queryKey: trpc.scholarships.searchScholarshipsEnhanced.queryKey() });
        queryClient.invalidateQueries({ queryKey: trpc.scholarships.getScholarship.queryKey() });
      }
    },
    options,
  );

/**
 * Delete a school scholarship
 */
export const useDeleteSchoolScholarship = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    {
      ...trpc.schoolCustomScholarships.deleteSchoolScholarship.mutationOptions(),
      onSuccess: (_data, _variables, _context) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: trpc.schoolCustomScholarships.getSchoolScholarships.queryKey() });
        queryClient.invalidateQueries({ queryKey: trpc.scholarships.searchScholarshipsEnhanced.queryKey() });
      }
    },
    options,
  );

// ============= SHORT COURSES API HOOKS =============

/**
 * Get all short courses (public - no auth required)
 * For discovery and browsing of available short courses
 */
export const useGetShortCourses = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.shortCourses.getShortCourses.queryOptions(undefined, {
      staleTime: 10 * 60 * 1000, // 10 minutes - courses change infrequently
    }),
    options,
  );

/**
 * Get a specific short course by ID (public - no auth required)
 * For viewing course details and submission form
 */
export const useGetShortCourse = (
  courseId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.shortCourses.getShortCourse.queryOptions(
      { courseId: courseId ?? "" },
      {
        enabled: (options?.enabled ?? true) && !!courseId,
        staleTime: 10 * 60 * 1000, // 10 minutes
      },
    ),
    options,
  );



// ============= ZUBE AGENT SHORT COURSES HOOKS =============

/**
 * Register interest using ZubeAgent system (replaces submitInterest)
 * Creates ZubeAgentStudent record for lead tracking and AI calls
 */
export const useRegisterShortCourseInterest = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.registerInterest.mutationOptions(),
    options,
  );

/**
 * Create or find existing student for AI call flow
 * Used before starting an AI conversation
 */
export const useCreateOrFindStudent = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.createOrFindStudent.mutationOptions(),
    options,
  );

/**
 * Generate ElevenLabs token with course context
 * Required for starting AI voice conversations
 */
export const useGenerateCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateCallToken.mutationOptions(),
    options,
  );

/**
 * Register call ID when conversation starts
 * Creates conversation record with 'zube' channel type
 */
export const useRegisterCallId = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.registerCallId.mutationOptions(),
    options,
  );

/**
 * Check if student has already registered interest for a specific short course
 * Returns whether the student has registered and their ID
 */
export const useCheckStudentInterest = (
  courseId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.shortCourses.checkStudentInterest.queryOptions(
      { courseId: courseId ?? "" },
      {
        enabled: (options?.enabled ?? true) && !!courseId,
        staleTime: 30 * 1000, // 30 seconds - interest status can change
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? false, // Fail quietly by default
      ...options,
    },
  ) as ReturnType<typeof useWrappedQuery<ApiResponse<{ hasRegisteredInterest: boolean; studentId?: string }>>>;

// ============= ZUBE AGENT API HOOKS =============

/**
 * Get current zube agent user
 */
export const useCurrentZubeAgentUser = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) => {
  const firebaseUser = getCurrentUser();
  const enabled = options?.enabled ?? !!firebaseUser;
  return useWrappedQuery(
    trpc.zubeAgents.user.getCurrentZubeAgentUser.queryOptions(undefined, {
      staleTime: 60 * 1000,
      retry: (failureCount: number, error: unknown) => {
        // Type assertion with safeguard
        if (!(error instanceof Error)) return failureCount < 1;
        const trpcError = error as unknown as { data: { httpStatus: number } };
        return trpcError.data?.httpStatus === 401 || trpcError.data?.httpStatus === 403 ? false : failureCount < 1;
      },
      enabled,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );
};

// ============= ZUBE AGENT CALLS HOOKS =============

/**
 * Get inbound call configurations
 */
export const useGetInboundConfigs = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.calls.getInboundConfigs.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get outbound call configurations
 */
export const useGetOutboundConfigs = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.calls.getOutboundConfigs.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Add inbound call configuration
 */
export const useAddInboundConfig = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.addInboundConfig.mutationOptions(),
    options,
  );

/**
 * Add outbound call configuration
 */
export const useAddOutboundConfig = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.addOutboundConfig.mutationOptions(),
    options,
  );

/**
 * Update inbound call configuration
 */
export const useUpdateInboundConfig = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.updateInboundConfig.mutationOptions(),
    options,
  );

/**
 * Update outbound call configuration
 */
export const useUpdateOutboundConfig = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.updateOutboundConfig.mutationOptions(),
    options,
  );

/**
 * Delete inbound call configuration
 */
export const useDeleteInboundConfig = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.deleteInboundConfig.mutationOptions(),
    options,
  );

/**
 * Delete outbound call configuration
 */
export const useDeleteOutboundConfig = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.deleteOutboundConfig.mutationOptions(),
    options,
  );

/**
 * Copy outbound configuration to inbound
 */
export const useCopyToInbound = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.copyToInbound.mutationOptions(),
    options,
  );

/**
 * Copy inbound configuration to outbound
 */
export const useCopyToOutbound = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.copyToOutbound.mutationOptions(),
    options,
  );

// ============= PHONE NUMBER MANAGEMENT HOOKS =============

/**
 * Get all phone number configurations
 */
export const useGetPhoneNumbers = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.calls.getPhoneNumbers.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Add a new phone number configuration
 */
export const useAddPhoneNumber = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.addPhoneNumber.mutationOptions(),
    options,
  );

/**
 * Update phone number configuration by index
 */
export const useUpdatePhoneNumber = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.updatePhoneNumber.mutationOptions(),
    options,
  );

/**
 * Delete phone number configuration by index
 */
export const useDeletePhoneNumber = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.deletePhoneNumber.mutationOptions(),
    options,
  );

/**
 * Update call forwarding settings for a Telnyx phone number
 */
export const useUpdateForwardingSettings = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.updateForwardingSettings.mutationOptions(),
    options,
  );

/**
 * Update human transfer routing settings for a Telnyx phone number
 */
export const useUpdateHumanTransferRouting = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.calls.updateHumanTransferRouting.mutationOptions(),
    options,
  );

// ============= ZUBE AGENT CONTACTS HOOKS =============

/**
 * Get available phone numbers and email addresses for filtering contacts
 */
export const useZubeAgentContactFilters = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.user.getContactFilters.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000, // 5 minutes - filters don't change frequently
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

export interface ContactsFilterParams {
  page?: number;
  pageSize?: number;
  status?: string;
  search?: string;
  channels?: string[];
  sources?: Array<{
    channel: 'voice' | 'whatsapp' | 'email';
    value: string;
  }>;
  outcome?: string;
  dateRange?: 'today' | 'last7days' | 'last30days' | 'all';
  leadScoreRange?: 'no_score' | 'cold' | 'warm' | 'hot' | 'sales_ready' | 'converting';
  leadScoreRanges?: ('no_score' | 'cold' | 'warm' | 'hot' | 'sales_ready' | 'converting')[];
  sortBy?: 'name' | 'lastContactedAt' | 'status' | 'leadScore' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Get paginated contacts for the current zube agent user with filters
 */
export const useZubeAgentContacts = (
  filters?: ContactsFilterParams,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.user.getContacts.queryOptions(filters, {
      enabled: options?.enabled ?? true,
      staleTime: 30 * 1000, // 30 seconds - contact list changes frequently
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get recent conversations for the current zube agent user
 */
export const useZubeAgentRecentConversations = (
  limit?: number,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.user.getRecentConversations.queryOptions(
      limit ? { limit } : undefined,
      {
        enabled: options?.enabled ?? true,
        staleTime: 30 * 1000,
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get detailed contact information including conversations
 */
export const useZubeAgentContactDetails = (
  contactId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.user.getContactDetails.queryOptions(
      { contactId: contactId ?? "" },
      {
        enabled: (options?.enabled ?? true) && !!contactId,
        staleTime: 15 * 1000, // 15 seconds
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update contact status
 */
export const useUpdateContactStatus = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.user.updateContactStatus.mutationOptions({
      onSuccess: () => {
        // Invalidate contacts list and details
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.user.getContacts.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.user.getContactDetails.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update contact details (firstName, lastName, email, phone, leadSource)
 */
export const useUpdateContactDetails = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.user.updateContactDetails.mutationOptions({
      onSuccess: () => {
        // Invalidate contacts list and details
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.user.getContacts.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.user.getContactDetails.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get audit logs for a specific target (optionally filtered by conversation)
 */
export const useZubeAgentAuditLogs = (
  targetId?: string,
  conversationId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.auditTrail.getLogsByTargetId.queryOptions(
      { targetId: targetId ?? "", conversationId: conversationId || undefined },
      {
        enabled: (options?.enabled ?? true) && !!targetId,
        staleTime: 10 * 1000,
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get urgent logs for dashboard
 */
export const useZubeAgentUrgentLogs = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.auditTrail.getUrgentUnacknowledgedLogs.queryOptions(
      {},
      {
        enabled: options?.enabled ?? true,
        staleTime: 30 * 1000,
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Acknowledge an audit log
 */
export const useAcknowledgeAuditLog = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.auditTrail.acknowledgeLog.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.auditTrail.getUrgentUnacknowledgedLogs.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.auditTrail.getLogsByTargetId.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.auditTrail.getEscalationLogs.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Unacknowledge an audit log (revert to unacknowledged state)
 */
export const useUnacknowledgeAuditLog = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.auditTrail.unacknowledgeLog.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.auditTrail.getUrgentUnacknowledgedLogs.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.auditTrail.getLogsByTargetId.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.auditTrail.getEscalationLogs.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get escalation logs with severity filtering
 */
export const useEscalationLogs = (
  params?: {
    categories?: ('positive_intent' | 'human_requested' | 'needs_assistance' | 'guardrail' | 'system_issue' | 'informational')[];
    excludeCategories?: ('positive_intent' | 'human_requested' | 'needs_assistance' | 'guardrail' | 'system_issue' | 'informational')[];
    acknowledged?: boolean;
    dateRangeStart?: string;
    dateRangeEnd?: string;
    limit?: number;
    offset?: number;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.auditTrail.getEscalationLogs.queryOptions(
      params ?? {},
      {
        enabled: options?.enabled ?? true,
        staleTime: 30_000,
        refetchOnWindowFocus: true,
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

// ============= ZUBE AGENT REMINDERS HOOKS =============

export const useZubeAgentReminders = (
  params?: {
    status?: 'pending' | 'fired' | 'cancelled';
    targetId?: string;
    dateRangeStart?: string;
    dateRangeEnd?: string;
    page?: number;
    pageSize?: number;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.reminders.getReminders.queryOptions(
      params ?? {},
      {
        enabled: options?.enabled ?? true,
        staleTime: 30 * 1000,
      },
    ),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

export const useZubeAgentUpcomingReminders = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.reminders.getUpcomingReminders.queryOptions(
      { days: 7 },
      {
        enabled: options?.enabled ?? true,
        staleTime: 30 * 1000,
      },
    ),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

export const useZubeAgentRemindersByTarget = (
  targetId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.reminders.getRemindersByTargetId.queryOptions(
      { targetId: targetId ?? "" },
      {
        enabled: (options?.enabled ?? true) && !!targetId,
        staleTime: 15 * 1000,
      },
    ),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

export const useCreateZubeAgentReminder = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.reminders.createReminder.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getUpcomingReminders.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getRemindersByTargetId.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getReminders.queryKey(),
        });
      },
    }),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

export const useUpdateZubeAgentReminder = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.reminders.updateReminder.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getUpcomingReminders.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getRemindersByTargetId.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getReminders.queryKey(),
        });
      },
    }),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

export const useCancelZubeAgentReminder = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.reminders.cancelReminder.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getUpcomingReminders.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getRemindersByTargetId.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getReminders.queryKey(),
        });
      },
    }),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

export const useCompleteZubeAgentReminder = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.reminders.completeReminder.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getUpcomingReminders.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getRemindersByTargetId.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.reminders.getReminders.queryKey(),
        });
      },
    }),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

// ============= AI TEMPLATES HOOKS =============

/**
 * Get all AI templates for the current user
 */
export const useAiTemplates = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.aiTemplates.getTemplates.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get a specific AI template by ID
 */
export const useAiTemplate = (
  templateId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.aiTemplates.getTemplateById.queryOptions(
      { id: templateId ?? "" },
      {
        enabled: (options?.enabled ?? true) && !!templateId,
        staleTime: 60 * 1000,
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Create a new AI template
 */
export const useCreateAiTemplate = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.aiTemplates.createTemplate.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.aiTemplates.getTemplates.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update an existing AI template
 */
export const useUpdateAiTemplate = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.aiTemplates.updateTemplate.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.aiTemplates.getTemplates.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.aiTemplates.getTemplateById.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Delete an AI template
 */
export const useDeleteAiTemplate = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.aiTemplates.deleteTemplate.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.aiTemplates.getTemplates.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Test an AI agent template reply via internal webhook
 */
export const useTestAgentReply = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.aiTemplates.testAgentReply.mutationOptions({}),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

/**
 * Upload contacts via manual CSV/Excel import
 */
export const useManualUploadContacts = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.manualImport.uploadContacts.mutationOptions(),
    options
  );

/**
 * Upload a single contact via manual entry
 */
export const useManualUploadSingleContact = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.manualImport.uploadSingleContact.mutationOptions(),
    options
  );

/**
 * Delete a target contact (internal users only)
 */
export const useDeleteTarget = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.user.deleteTarget.mutationOptions(),
    options
  );

// ============= AGENTIC MESSAGING TEMPLATES HOOKS =============

/**
 * Get all agentic messaging templates for the current user
 */
export const useAgenticMessagingTemplates = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.agenticMessagingTemplates.getTemplates.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get a specific agentic messaging template by ID
 */
export const useAgenticMessagingTemplate = (
  templateId?: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.agenticMessagingTemplates.getTemplateById.queryOptions(
      { id: templateId ?? "" },
      {
        enabled: (options?.enabled ?? true) && !!templateId,
        staleTime: 60 * 1000,
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Create a new agentic messaging template
 */
export const useCreateAgenticMessagingTemplate = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.agenticMessagingTemplates.createTemplate.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.agenticMessagingTemplates.getTemplates.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update an existing agentic messaging template
 */
export const useUpdateAgenticMessagingTemplate = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.agenticMessagingTemplates.updateTemplate.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.agenticMessagingTemplates.getTemplates.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.agenticMessagingTemplates.getTemplateById.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Delete an agentic messaging template
 */
export const useDeleteAgenticMessagingTemplate = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.agenticMessagingTemplates.deleteTemplate.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.agenticMessagingTemplates.getTemplates.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Generate a graph definition using AI
 */
export const useGenerateAgenticGraph = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.agenticMessagingTemplates.generateGraphDefinition.mutationOptions({}),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

// ============= KNOWLEDGE BASE HOOKS =============

/**
 * List all knowledge entries
 */
export const useListKnowledgeEntries = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.knowledgeBase.listKnowledgeEntries.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Get details of a specific knowledge entry
 */
export const useGetKnowledgeEntryDetails = (
  fileId: string,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.knowledgeBase.getKnowledgeEntryDetails.queryOptions(
      { fileId },
      {
        enabled: (options?.enabled ?? true) && !!fileId,
      },
    ),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Delete a knowledge entry
 */
export const useDeleteKnowledgeEntry = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.knowledgeBase.deleteKnowledgeEntry.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.knowledgeBase.listKnowledgeEntries.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Delete all knowledge entries (internal users only)
 */
export const useDeleteAllKnowledgeEntries = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.knowledgeBase.deleteAllKnowledgeEntries.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.knowledgeBase.listKnowledgeEntries.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Get upload URL for knowledge base file
 */
export const useGetKnowledgeUploadUrl = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.knowledgeBase.getUploadUrl.mutationOptions(),
    options,
  );

// ============= ZOHO CRM INTEGRATION HOOKS =============

/**
 * Get Zoho account information (connection status, API domain, scopes)
 */
export const useZohoAccountInfo = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.zoho.getAccountInfo.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    options
  );

/**
 * List available Zoho CRM modules (Contacts, Accounts, Deals, Leads, etc.)
 */
export const useZohoListObjects = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.zoho.listObjects.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000, // 5 minutes - modules don't change frequently
    }),
    options
  );

/**
 * Get schema for a specific Zoho module
 */
export const useZohoObjectSchema = (
  objectType: string,
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.zoho.getObjectSchema.queryOptions(
      { objectType },
      {
        enabled: (options?.enabled ?? true) && !!objectType,
        staleTime: 10 * 60 * 1000, // 10 minutes
      }
    ),
    options
  );

/**
 * Get a sample record from Zoho module for preview
 */
export const useZohoSampleRecord = (
  params: { objectType: string; properties?: string[] },
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.zoho.getSampleRecord.queryOptions(params, {
      enabled: (options?.enabled ?? true) && !!params.objectType,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
    options
  );

/**
 * Disconnect Zoho integration
 */
export const useZohoDisconnect = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.zoho.disconnect.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.zoho.getAccountInfo.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Get field mappings for Zoho modules
 */
export const useZohoFieldMappings = (
  params?: { objectType?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.zoho.getFieldMappings.queryOptions(params || {}, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
    options
  );

/**
 * Save field mapping configuration for a Zoho module
 */
export const useZohoSaveFieldMapping = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.zoho.saveFieldMapping.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.zoho.getFieldMappings.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Remove field mapping configuration for a Zoho module
 */
export const useZohoRemoveFieldMapping = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.zoho.removeFieldMapping.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.zoho.getFieldMappings.queryKey(),
        });
      },
    }),
    options
  );

// ============= SALESFORCE CRM INTEGRATION HOOKS =============

/**
 * Save Salesforce External Client App credentials and get OAuth URL
 */
export const useSalesforceSaveClientCredentials = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.salesforce.saveClientCredentials.mutationOptions(),
    options
  );

/**
 * Get Salesforce account information (connection status, instance URL)
 */
export const useSalesforceAccountInfo = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.salesforce.getAccountInfo.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    options
  );

/**
 * List available Salesforce objects (Contact, Lead, Account, Opportunity, Case + custom)
 */
export const useSalesforceListObjects = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.salesforce.listObjects.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
    options
  );

/**
 * Get schema for a specific Salesforce object
 */
export const useSalesforceObjectSchema = (
  objectType: string,
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.salesforce.getObjectSchema.queryOptions(
      { objectType },
      {
        enabled: (options?.enabled ?? true) && !!objectType,
        staleTime: 10 * 60 * 1000, // 10 minutes
      }
    ),
    options
  );

/**
 * Get a sample record from Salesforce object for preview
 */
export const useSalesforceSampleRecord = (
  params: { objectType: string; properties?: string[] },
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.salesforce.getSampleRecord.queryOptions(params, {
      enabled: (options?.enabled ?? true) && !!params.objectType,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
    options
  );

/**
 * Disconnect Salesforce integration
 */
export const useSalesforceDisconnect = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.salesforce.disconnect.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.salesforce.getAccountInfo.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Get field mappings for Salesforce objects
 */
export const useSalesforceFieldMappings = (
  params?: { objectType?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.salesforce.getFieldMappings.queryOptions(params || {}, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
    options
  );

/**
 * Save field mapping configuration for a Salesforce object
 */
export const useSalesforceSaveFieldMapping = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.salesforce.saveFieldMapping.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.salesforce.getFieldMappings.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Remove field mapping configuration for a Salesforce object
 */
export const useSalesforceRemoveFieldMapping = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.salesforce.removeFieldMapping.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.salesforce.getFieldMappings.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Process uploaded knowledge base file
 */
export const useProcessKnowledgeFile = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.knowledgeBase.processUploadedFile.mutationOptions(),
    options,
  );

/**
 * Process URL for knowledge base
 */
export const useProcessKnowledgeUrl = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.knowledgeBase.processUrl.mutationOptions(),
    options,
  );

/**
 * Bulk create text entries for knowledge base
 */
export const useBulkCreateTextEntries = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.knowledgeBase.bulkCreateTextEntries.mutationOptions(),
    options,
  );

/**
 * Sync knowledge base to ElevenLabs
 */
export const useSyncKnowledgeBaseToElevenLabs = (
  options?: WrappedMutationOptions,
) =>
  useWrappedMutation(
    trpc.zubeAgents.knowledgeBase.syncToElevenLabs.mutationOptions(),
    options,
  );

// ============= EMAIL INTEGRATION HOOKS =============

// ============= ADMIN CLIENT HOOKS =============

/**
 * List all client accounts (admin only)
 */
export const useListAllClients = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.admin.listAllClients.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    options,
  );

/**
 * Send login credentials to a client
 */
export const useSendClientCredentials = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.admin.sendClientCredentials.mutationOptions(),
    options,
  );

/**
 * Create a new client account
 */
export const useCreateClientAccount = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.admin.createClientAccount.mutationOptions(),
    options,
  );

// ============= COMPANY PROFILE HOOKS =============

/**
 * Get company profile
 */
export const useGetCompanyProfile = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.user.getCompanyProfile.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
    }),
    options,
  );

/**
 * Update company profile
 */
export const useUpdateCompanyProfile = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.user.updateCompanyProfile.mutationOptions(),
    options,
  );

// ============= API INTEGRATION HOOKS =============

/**
 * Get API settings (API key, webhook URL, webhook secret)
 */
export const useApiSettings = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.api.getApiSettings.queryOptions(undefined, {
      staleTime: 60 * 1000,
    }),
    options,
  );

/**
 * Generate API key and webhook secret
 */
export const useGenerateApiKey = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.api.generateApiKey.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.api.getApiSettings.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Update webhook URL
 */
export const useUpdateWebhookUrl = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.api.updateWebhookUrl.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.api.getApiSettings.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Regenerate webhook secret
 */
export const useRegenerateWebhookSecret = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.api.regenerateWebhookSecret.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.api.getApiSettings.queryKey(),
        });
      },
    }),
    options,
  );

// ============= CAL.COM HOOKS =============

/**
 * Get Cal.com integration settings
 */
export const useCalDotComSettings = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.calDotCom.getSettings.queryOptions(undefined, {
      staleTime: 60 * 1000,
    }),
    options,
  );

/**
 * Connect Cal.com integration
 */
export const useConnectCalDotCom = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.calDotCom.connect.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.calDotCom.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Disconnect Cal.com integration
 */
export const useDisconnectCalDotCom = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.calDotCom.disconnect.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.calDotCom.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * List Cal.com event types from the provider
 */
export const useCalDotComListEventTypes = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.calDotCom.listEventTypes.queryOptions(undefined, {
      staleTime: 5 * 60 * 1000,
      enabled: false,
    }),
    options,
  );

/**
 * Test Cal.com slot listing
 */
export const useCalDotComTestListSlots = (
  input: { eventTypeId: string },
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.calDotCom.testListSlots.queryOptions(input, {
      staleTime: 0,
      enabled: false,
    }),
    options,
  );

// ============= CALENDLY HOOKS =============

/**
 * Get Calendly integration settings
 */
export const useCalendlySettings = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.calendly.getSettings.queryOptions(undefined, {
      staleTime: 60 * 1000,
    }),
    options,
  );

/**
 * Disconnect Calendly integration
 */
export const useDisconnectCalendly = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.calendly.disconnect.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.calendly.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * List Calendly event types from the provider
 */
export const useCalendlyListEventTypes = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.calendly.listEventTypes.queryOptions(undefined, {
      staleTime: 5 * 60 * 1000,
      enabled: false,
    }),
    options,
  );

/**
 * Test Calendly slot listing
 */
export const useCalendlyTestListSlots = (
  input: { eventTypeId: string },
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.calendly.testListSlots.queryOptions(input, {
      staleTime: 0,
      enabled: false,
    }),
    options,
  );

// ============= MICROSOFT CALENDAR HOOKS =============

/**
 * Get Microsoft Calendar integration settings
 */
export const useMicrosoftCalendarSettings = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.microsoftCalendar.getSettings.queryOptions(undefined, {
      staleTime: 60 * 1000,
    }),
    options,
  );

/**
 * Disconnect Microsoft Calendar integration
 */
export const useDisconnectMicrosoftCalendar = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.microsoftCalendar.disconnect.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.microsoftCalendar.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

// ============= ZOOM CALENDAR HOOKS =============

/**
 * Get Zoom Calendar integration settings
 */
export const useZoomCalendarSettings = (options?: WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.zoomCalendar.getSettings.queryOptions(undefined, {
      staleTime: 60 * 1000,
    }),
    options,
  );

/**
 * Disconnect Zoom Calendar integration
 */
export const useDisconnectZoomCalendar = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.zoomCalendar.disconnect.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.zoomCalendar.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

// ============= CALENDAR SHARED SETTINGS HOOKS =============

/**
 * Update Cal.com calendar shared settings
 */
export const useUpdateCalDotComCalendarSettings = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.calDotCom.updateCalendarSettings.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.calDotCom.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Update Calendly calendar shared settings
 */
export const useUpdateCalendlyCalendarSettings = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.calendly.updateCalendarSettings.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.calendly.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Update Microsoft Calendar shared settings
 */
export const useUpdateMicrosoftCalendarSettings = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.microsoftCalendar.updateCalendarSettings.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.microsoftCalendar.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Update Microsoft Calendar event templates
 */
export const useUpdateMicrosoftCalendarEventTemplates = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.microsoftCalendar.updateEventTemplates.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.microsoftCalendar.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Update Zoom Calendar shared settings
 */
export const useUpdateZoomCalendarSettings = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.zoomCalendar.updateCalendarSettings.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.zoomCalendar.getSettings.queryKey(),
        });
      },
    }),
    options,
  );

// ============= DEMO HOOKS =============

/**
 * Generate demo call token
 */
export const useGenerateDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate hotel demo call token
 */
export const useGenerateHotelDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateHotelDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate LBF Travel demo call token
 */
export const useGenerateLBFTravelDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateLBFTravelDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate MiCampus demo call token
 */
export const useGenerateMiCampusDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateMiCampusDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate Europcar demo call token
 */
export const useGenerateEuropcarDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateEuropcarDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate Casa Andina demo call token
 */
export const useGenerateCasaAndinaDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateCasaAndinaDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate Corinthia London demo call token
 */
export const useGenerateCorinthiaDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateCorinthiaDemoCallToken.mutationOptions(),
    options,
  );

export const useGenerateDeyaarDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateDeyaarDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate MGM Macau demo call token
 */
export const useGenerateMGMMacauDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateMGMMacauDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate SunLife Mauritius demo call token
 */
export const useGenerateSunLifeDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateSunLifeDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate Yugo demo call token
 */
export const useGenerateYugoDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateYugoDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate Vamos demo call token
 */
export const useGenerateVamosDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generateVamosDemoCallToken.mutationOptions(),
    options,
  );

/**
 * Generate PHH (Parks Hospitality Holdings) demo call token
 */
export const useGeneratePHHDemoCallToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.shortCourses.generatePHHDemoCallToken.mutationOptions(),
    options,
  );

// ============= ZUBE AGENT INSIGHTS HOOKS =============

/**
 * Get overview KPIs for insights dashboard
 */
export const useInsightsOverviewKPIs = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getOverviewKPIs.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  }),
  options
);

/**
 * Get daily trend data for charts
 */
export const useInsightsDailyTrend = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string; metric: 'contacts' | 'pickupRate' | 'responseTime' | 'leadScore' | 'transferRate' },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getDailyTrend.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000, // 15 minutes
  }),
  options
);

/**
 * Get enhanced daily trend for dual-axis chart with unique contacts and activity metrics
 */
export const useInsightsDailyTrendEnhanced = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getDailyTrendEnhanced.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000, // 15 minutes
  }),
  options
);

/**
 * Get performance page trend data for messaging channels
 */
export const useInsightsMessagingPerformanceTrend = (
  params: { dateFrom: string; dateTo: string; channel: MessagingChannelType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getMessagingPerformanceTrend.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000, // 15 minutes
  }),
  options
);

/**
 * Get content summary (top items per insight type)
 */
export const useInsightsContentSummary = (
  params: { dateFrom: string; dateTo: string; workflowId?: string; limitPerType?: number },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getContentSummary.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get channel-specific metrics
 */
export const useInsightsChannelMetrics = (
  params: { dateFrom: string; dateTo: string; channel: ChannelType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getChannelMetrics.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  }),
  options
);

/**
 * Get lead score distribution
 */
export const useInsightsLeadScoreDistribution = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getLeadScoreDistribution.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get day of week analysis
 */
export const useInsightsDayOfWeekAnalysis = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getDayOfWeekAnalysis.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get content insights for a specific type
 */
export const useInsightsContentInsights = (
  params: { dateFrom: string; dateTo: string; insightType: 'concerns' | 'competitors' | 'interests' | 'unanswered_questions' | 'decision_factors' | 'caller_profiles'; workflowId?: string; limit?: number; offset?: number },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getContentInsights.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get category trend
 */
export const useInsightsCategoryTrend = (
  params: { dateFrom: string; dateTo: string; insightType: 'concerns' | 'competitors' | 'interests' | 'unanswered_questions' | 'decision_factors' | 'caller_profiles'; categoryCode: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getCategoryTrend.queryOptions(params, {
    enabled: (options?.enabled ?? true) && !!params.categoryCode,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get hourly heatmap data
 */
export const useInsightsHourlyHeatmap = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string; metric: 'volume' | 'pickupRate' | 'responseTime' },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getHourlyHeatmap.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get time recommendations
 */
export const useInsightsTimeRecommendations = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getTimeRecommendations.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Compare workflows
 */
export const useInsightsCompareWorkflows = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowIds: string[] },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.compareWorkflows.queryOptions(params, {
    enabled: (options?.enabled ?? true) && params.workflowIds.length >= 2,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Compare workflow content
 */
export const useInsightsCompareWorkflowContent = (
  params: { dateFrom: string; dateTo: string; insightType: 'concerns' | 'competitors' | 'interests' | 'unanswered_questions' | 'decision_factors' | 'caller_profiles'; workflowIds: string[]; limit?: number },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.compareWorkflowContent.queryOptions(params, {
    enabled: (options?.enabled ?? true) && params.workflowIds.length >= 2,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * List insight categories
 */
export const useInsightsCategories = (
  params?: { insightType?: 'concerns' | 'competitors' | 'interests' | 'unanswered_questions' | 'decision_factors' | 'caller_profiles' | 'custom' | 'dropoff_reason' | 'outcome' | 'sentiment_dissatisfaction'; customLabel?: string; includeInactive?: boolean },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.listCategories.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 60 * 60 * 1000, // 1 hour - categories rarely change
  }),
  options
);

/**
 * Create category
 */
export const useInsightsCreateCategory = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.insights.createCategory.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.insights.listCategories.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Update category
 */
export const useInsightsUpdateCategory = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.insights.updateCategory.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.insights.listCategories.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Delete category
 */
export const useInsightsDeleteCategory = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.insights.deleteCategory.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.insights.listCategories.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Analyze other bucket
 */
export const useInsightsAnalyzeOtherBucket = (
  params: { dateFrom: string; dateTo: string; insightType: 'concerns' | 'competitors' | 'interests' | 'unanswered_questions' | 'decision_factors' | 'caller_profiles' | 'dropoff_reason' | 'outcome' | 'sentiment_dissatisfaction'; minOccurrences?: number },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.analyzeOtherBucket.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get available workflows
 */
export const useInsightsAvailableWorkflows = (
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getAvailableWorkflows.queryOptions(undefined, {
    enabled: options?.enabled ?? true,
    staleTime: 60 * 60 * 1000, // 1 hour
  }),
  options
);

/**
 * Check if user has insights data
 */
export const useInsightsHasData = (
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.hasInsightsData.queryOptions(undefined, {
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  }),
  options
);

// ============= DROP-OFF ANALYTICS HOOKS =============

/**
 * Check if user has any drop-off data
 */
export const useInsightsHasDropoffData = (
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.hasDropoffData.queryOptions(undefined, {
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  }),
  options
);

/**
 * Get drop-off overview KPIs with comparison to previous period
 */
export const useInsightsDropoffKPIs = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getDropoffOverviewKPIs.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  }),
  options
);

/**
 * Get drop-off counts grouped by reason category
 */
export const useInsightsDropoffByReason = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getDropoffByReason.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get drop-off counts grouped by channel
 */
export const useInsightsDropoffByChannel = (
  params: { dateFrom: string; dateTo: string; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getDropoffByChannel.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get drop-off counts grouped by sentiment band
 */
export const useInsightsDropoffBySentiment = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getDropoffBySentiment.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get daily drop-off trend data (drop-offs, valid closes, rate per day)
 */
export const useInsightsDropoffDailyTrend = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getDropoffDailyTrend.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get thread classification split (dropoff, valid_close, ongoing, etc.)
 */
export const useInsightsDropoffClassificationSplit = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getDropoffClassificationSplit.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Analyze the "Other" bucket for drop-off reasons and suggest new categories
 */
export const useInsightsAnalyzeDropoffOtherBucket = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string; minOccurrences?: number },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.analyzeDropoffOtherBucket.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get content insights with comparison to previous period
 * Returns rows with delta values and summary with comparison metrics
 */
export const useInsightsContentInsightsWithComparison = (
  params: {
    dateFrom: string;
    dateTo: string;
    insightType: 'concerns' | 'competitors' | 'interests' | 'unanswered_questions' | 'decision_factors' | 'caller_profiles' | 'custom';
    workflowId?: string;
    customLabel?: string;  // Required when insightType='custom'
    comparisonPeriod: 'none' | 'previousPeriod' | 'lastMonth' | 'lastYear';
    limit?: number;
    offset?: number;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getContentInsightsWithComparison.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get detailed category information including raw terms/verbatims
 * Used in category detail modal to show "What People Said"
 */
export const useInsightsCategoryDetail = (
  params: {
    dateFrom: string;
    dateTo: string;
    insightType: 'concerns' | 'competitors' | 'interests' | 'unanswered_questions' | 'decision_factors' | 'caller_profiles' | 'custom';
    categoryCode: string;
    workflowId?: string;
    customLabel?: string;  // Required when insightType='custom'
    comparisonPeriod?: 'none' | 'previousPeriod' | 'lastMonth' | 'lastYear';
    maxRawTerms?: number;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getCategoryDetail.queryOptions(params, {
    enabled: (options?.enabled ?? true) && !!params.categoryCode,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get full category detail for enhanced modal view.
 * Includes previous period stats, anomaly detection, and total mentions context.
 */
export const useInsightsCategoryFullDetail = (
  params: {
    dateFrom: string;
    dateTo: string;
    insightType: 'concerns' | 'competitors' | 'interests' | 'unanswered_questions' | 'decision_factors' | 'caller_profiles' | 'custom';
    categoryCode: string;
    workflowId?: string;
    customLabel?: string;  // Required when insightType='custom'
    comparisonPeriod?: 'none' | 'previousPeriod' | 'lastMonth' | 'lastYear';
    maxRawTerms?: number;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getCategoryFullDetail.queryOptions(params, {
    enabled: (options?.enabled ?? true) && !!params.categoryCode,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Streaming NLQ query hook
 * Executes natural language queries against content insights with streaming progress updates
 *
 * @returns {Object} Hook result with:
 *   - execute: Function to start a query
 *   - status: Current streaming status ('idle' | 'initializing' | 'planning' | 'executing' | 'formatting' | 'complete' | 'error')
 *   - message: Current status message
 *   - result: Final query result (only available when status is 'complete')
 *   - error: Error details (only available when status is 'error')
 *   - isLoading: Whether a query is in progress
 *   - reset: Function to reset to idle state
 */
export function useStreamingNLQ() {
  const [status, setStatus] = useState<'idle' | 'initializing' | 'planning' | 'executing' | 'formatting' | 'complete' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');
  const [result, setResult] = useState<import('@repo/trpc').NLQExecutionResponse | null>(null);
  const [error, setError] = useState<{ code: string; message: string; suggestions?: string[] } | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const execute = useCallback(async (params: {
    query: string;
    dateFrom: string;
    dateTo: string;
    workflowId: string | null;
  }) => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    // Reset state
    setStatus('initializing');
    setMessage('Preparing your query...');
    setResult(null);
    setError(null);

    try {
      // Use the base tRPC client to call the streaming query
      // The httpBatchStreamLink returns an async iterable for async generators
      const response = await baseTrpcClient.zubeAgents.insights.executeNaturalLanguageQuery.query(params);

      // The response is an async iterable when using httpBatchStreamLink with async generators
      if (Symbol.asyncIterator in (response as object)) {
        for await (const event of response as AsyncIterable<import('@repo/trpc').NLQStreamEvent>) {
          if (abortControllerRef.current?.signal.aborted) {
            break;
          }

          if (event.status === 'complete') {
            setStatus('complete');
            setMessage('');
            setResult(event.data);
          } else if (event.status === 'error') {
            setStatus('error');
            setMessage(event.message);
            setError({ code: 'STREAM_ERROR', message: event.message });
          } else {
            setStatus(event.status);
            setMessage(event.message);
          }
        }
      } else {
        // Fallback for non-streaming response (shouldn't happen with proper setup)
        console.warn('[useStreamingNLQ] Got non-iterable response, treating as single result');
        const singleResult = response as unknown as import('@repo/trpc').NLQStreamEvent;
        if (singleResult.status === 'complete') {
          setStatus('complete');
          setResult(singleResult.data);
        } else {
          setStatus(singleResult.status as 'error');
          setMessage(singleResult.message);
        }
      }
    } catch (err) {
      if (abortControllerRef.current?.signal.aborted) {
        // Request was intentionally aborted, don't show error
        return;
      }

      console.error('[useStreamingNLQ] Error:', err);
      setStatus('error');
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setMessage(errorMessage);
      setError({ code: 'UNKNOWN_ERROR', message: errorMessage });
    }
  }, []);

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setStatus('idle');
    setMessage('');
    setResult(null);
    setError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    execute,
    status,
    message,
    result,
    error,
    isLoading: status !== 'idle' && status !== 'complete' && status !== 'error',
    reset,
  };
}

/**
 * Get suggested queries for the NLQ interface
 * Returns contextual suggestions based on user's data
 */
export const useInsightsGetSuggestedQueries = (
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getSuggestedQueries.queryOptions(undefined, {
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  }),
  options
);

// ============================================
// CUSTOM ANALYZER HOOKS
// ============================================

/**
 * Get available custom content labels for a date range and workflow.
 * Returns labels from custom insight types that have data in the specified period.
 * Used by the dashboard to populate custom tabs.
 */
export const useInsightsCustomContentLabels = (
  params: {
    dateFrom: string;
    dateTo: string;
    workflowId?: string;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getCustomContentLabels.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes per date range + workflow
  }),
  options
);

// ============================================
// LEAD SCORE CORRELATION & INTERSECTION HOOKS
// ============================================

type InsightType = 'concerns' | 'competitors' | 'interests' | 'unanswered_questions' | 'decision_factors' | 'caller_profiles' | 'custom';

/**
 * Get content insights enriched with lead score correlation data.
 * Returns hot/cold lead percentages and conversion signal for each category.
 */
export const useInsightsLeadScoreCorrelation = (
  params: {
    dateFrom: string;
    dateTo: string;
    insightType: InsightType;
    workflowId?: string;
    customLabel?: string;  // Required when insightType='custom'
    categoryCode?: string;
    limit?: number;
    offset?: number;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getLeadScoreCorrelation.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get exact overlap between two insight categories.
 * Uses rollupId arrays for exact intersection or HLL estimates.
 */
export const useInsightsIntersection = (
  params: {
    dateFrom: string;
    dateTo: string;
    setA: { insightType: InsightType; categoryCode: string };
    setB: { insightType: InsightType; categoryCode: string };
    workflowId?: string;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getInsightIntersection.queryOptions(params, {
    enabled: (options?.enabled ?? true) && !!params.setA?.categoryCode && !!params.setB?.categoryCode,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get conversion drivers - categories ranked by hot lead percentage.
 * Returns both drivers (high hot lead %) and blockers (high cold lead %).
 */
export const useInsightsConversionDrivers = (
  params: {
    dateFrom: string;
    dateTo: string;
    insightType?: InsightType;
    workflowId?: string;
    customLabel?: string;  // Required when insightType='custom'
    minOccurrences?: number;
    limit?: number;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getConversionDrivers.queryOptions(params, {
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get exact unique count using rollupId arrays when available.
 * Falls back to HLL estimation if rollupIds are not populated.
 */
export const useInsightsExactUniqueCount = (
  params: {
    dateFrom: string;
    dateTo: string;
    insightType: InsightType;
    categoryCode: string;
    workflowId?: string;
  },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getExactUniqueCount.queryOptions(params, {
    enabled: (options?.enabled ?? true) && !!params.categoryCode,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Get category trend with hot lead ratio per time bucket.
 * Extends getCategoryTrend with lead score breakdown and trend analysis.
 */
export const useInsightsCategoryTrendWithLeadScore = (
  params: {
    dateFrom: string;
    dateTo: string;
    insightType: InsightType;
    categoryCode: string;
    workflowId?: string;
    customLabel?: string;  // Required when insightType='custom'
  },
  options?: { enabled?: boolean } & WrappedQueryOptions
) => useWrappedQuery(
  trpc.zubeAgents.insights.getCategoryTrendWithLeadScore.queryOptions(params, {
    enabled: (options?.enabled ?? true) && !!params.categoryCode,
    staleTime: 15 * 60 * 1000,
  }),
  options
);

/**
 * Check if user has insights data and get the oldest date
 */
export const useHasInsightsData = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.insights.hasInsightsData.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 60 * 1000, // 1 hour - data availability doesn't change often
    }),
    options
  );

/**
 * Get homepage metrics for the agent dashboard
 * Returns 1-4 dynamic KPIs based on data thresholds
 */
export const useHomepageMetrics = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.insights.getHomepageMetrics.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
    options
  );

// ============= META MESSAGING INTEGRATION HOOKS =============

/**
 * List all connected Facebook Pages for the user (Messenger integration)
 */
export const useFacebookPages = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.metaMessaging.listFacebookPages.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    options
  );

/**
 * Disconnect a Facebook Page
 */
export const useDisconnectFacebookPage = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.metaMessaging.disconnectFacebookPage.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.metaMessaging.listFacebookPages.queryKey(),
        });
      },
    }),
    options
  );

/**
 * List all connected Instagram accounts for the user
 */
export const useInstagramAccounts = (options?: { enabled?: boolean } & WrappedQueryOptions) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.metaMessaging.listInstagramAccounts.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    options
  );

/**
 * Disconnect an Instagram account
 */
export const useDisconnectInstagramAccount = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.metaMessaging.disconnectInstagramAccount.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.metaMessaging.listInstagramAccounts.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Get OAuth URL for initiating Meta (Facebook/Instagram) OAuth flow
 */
export const useMetaMessagingOAuthUrl = (
  input: { platform: 'messenger' | 'instagram'; isPersonalOnboarding?: boolean },
  options?: { enabled?: boolean } & WrappedQueryOptions
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.metaMessaging.getOAuthUrl.queryOptions(input, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000, // 5 minutes - OAuth URLs don't change often
    }),
    options
  );

/**
 * Trigger history sync for Messenger or Instagram
 * User must manually trigger this after connecting - not auto-triggered during OAuth
 */
export const useTriggerMetaHistorySync = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.metaMessaging.triggerHistorySync.mutationOptions({
      onSuccess: () => {
        // Invalidate both lists as sync status may have changed
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.metaMessaging.listFacebookPages.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.metaMessaging.listInstagramAccounts.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Update channel permissions for a Facebook Page (Messenger)
 */
export const useUpdateMessengerChannelPermissions = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.metaMessaging.updateMessengerChannelPermissions.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.metaMessaging.listFacebookPages.queryKey(),
        });
      },
    }),
    options
  );

/**
 * Update channel permissions for an Instagram account
 */
export const useUpdateInstagramChannelPermissions = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.metaMessaging.updateInstagramChannelPermissions.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.metaMessaging.listInstagramAccounts.queryKey(),
        });
      },
    }),
    options
  );

// ============= EMAIL OAUTH INTEGRATION HOOKS =============

/**
 * Get email OAuth configuration (supports both Microsoft 365 and Google Workspace)
 */
export const useGetEmailOAuthConfig = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.integrations.emailOAuth.getConfig.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    options,
  );

/**
 * Disconnect email OAuth integration (either provider)
 */
export const useDisconnectEmailOAuth = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.emailOAuth.disconnect.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.emailOAuth.getConfig.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Test email OAuth connection
 */
export const useTestEmailOAuthConnection = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.emailOAuth.testConnection.mutationOptions(),
    options,
  );

/**
 * Trigger email history sync
 * @param syncDepthMonths - Number of months to sync (1-6, default 3)
 */
export const useTriggerEmailHistorySync = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.emailOAuth.triggerHistorySync.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.emailOAuth.getConfig.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Send a test email via connected OAuth provider
 */
export const useSendTestEmailOAuth = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.emailOAuth.sendTestEmail.mutationOptions(),
    options,
  );

/**
 * Update email OAuth channel permissions (read/send)
 */
export const useUpdateEmailOAuthChannelPermissions = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.emailOAuth.updateChannelPermissions.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.emailOAuth.getConfig.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Update email OAuth ignore list
 */
export const useUpdateEmailOAuthIgnoreList = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.emailOAuth.updateIgnoreList.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.emailOAuth.getConfig.queryKey(),
        });
      },
    }),
    options,
  );

/**
 * Update email OAuth forward bypass senders list
 */
export const useUpdateEmailOAuthForwardBypassSenders = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.integrations.emailOAuth.updateForwardBypassSenders.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.integrations.emailOAuth.getConfig.queryKey(),
        });
      },
    }),
    options,
  );

// ============= CUSTOM ANALYZERS HOOKS =============

/**
 * Get all custom analyzers for the current user
 */
export const useGetCustomAnalyzers = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.customAnalyzers.getCustomAnalyzers.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Create a new custom analyzer
 */
export const useCreateCustomAnalyzer = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.customAnalyzers.createCustomAnalyzer.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.customAnalyzers.getCustomAnalyzers.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update an existing custom analyzer
 */
export const useUpdateCustomAnalyzer = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.customAnalyzers.updateCustomAnalyzer.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.customAnalyzers.getCustomAnalyzers.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Delete a custom analyzer
 */
export const useDeleteCustomAnalyzer = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.customAnalyzers.deleteCustomAnalyzer.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.customAnalyzers.getCustomAnalyzers.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

// ============= ESCALATION RULES HOOKS =============

/**
 * Get all escalation rules for the current user
 */
export const useGetEscalationRules = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.escalation.getRules.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Create or update an escalation rule
 */
export const useUpsertEscalationRule = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.escalation.upsertRule.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.escalation.getRules.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Delete an escalation rule
 */
export const useDeleteEscalationRule = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.escalation.deleteRule.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.escalation.getRules.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Reorder escalation rules
 */
export const useReorderEscalationRules = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.escalation.reorderRules.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.escalation.getRules.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

// ============= PUSH TOKENS HOOKS =============

/**
 * Register a push notification token
 */
export const useRegisterPushToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.pushTokens.register.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.pushTokens.list.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Remove a push notification token
 */
export const useRemovePushToken = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.pushTokens.remove.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.pushTokens.list.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * Update the label of a push notification token
 */
export const useUpdatePushTokenLabel = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.pushTokens.updateLabel.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.pushTokens.list.queryKey(),
        });
      },
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

/**
 * List all push tokens for the current user
 */
export const useListPushTokens = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.pushTokens.list.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000, // 1 minute
    }),
    {
      showErrorSnackbar: options?.showErrorSnackbar ?? true,
    },
  );

// ============= MAGIC LINKS MEETINGS HOOKS =============

/**
 * Validate booking link token (public - no auth required)
 */
export const useBookingLinkValidation = (
  token: string | null,
  options?: WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.magicLinksMeetings.validateBookingLink.queryOptions(
      { token: token || '' },
      {
        enabled: !!token,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
        refetchOnWindowFocus: false,
      },
    ),
    options,
  );

/**
 * Get available time slots for booking
 */
export const useAvailableSlots = (
  params: {
    token: string;
    rangeStart: string;
    rangeEnd: string;
    timezone: string;
  } | null,
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.magicLinksMeetings.listAvailableSlots.queryOptions(
      params ?? { token: '', rangeStart: '', rangeEnd: '', timezone: '' },
      {
        enabled: (options?.enabled ?? true) && !!params,
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: true,
      },
    ),
    options,
  );

/**
 * Book a meeting (public - no auth required)
 */
export const useBookMeeting = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.magicLinksMeetings.bookMeeting.mutationOptions(),
    options,
  );

// ============= REVIVAL SYSTEM HOOKS =============

/**
 * Get revival settings for the current user
 */
export const useRevivalSettings = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.user.getRevivalSettings.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 60 * 1000,
    }),
    options,
  );

/**
 * Update revival settings
 */
export const useUpdateRevivalSettings = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.user.updateRevivalSettings.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.user.getRevivalSettings.queryKey(),
        });
      },
    }),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

/**
 * Pause revival for a specific target
 */
export const usePauseRevival = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.user.pauseRevival.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.user.getRevivalSettings.queryKey(),
        });
      },
    }),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

/**
 * Stop revival for a specific target
 */
export const useStopRevival = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.user.stopRevival.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.user.getRevivalSettings.queryKey(),
        });
      },
    }),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

/**
 * Skip revival for a specific target (jump to next stage)
 */
export const useSkipRevival = (options?: WrappedMutationOptions) =>
  useWrappedMutation(
    trpc.zubeAgents.user.skipRevival.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.zubeAgents.user.getRevivalSettings.queryKey(),
        });
      },
    }),
    { showErrorSnackbar: options?.showErrorSnackbar ?? true },
  );

/**
 * Check if user has any revival data
 */
export const useHasRevivalData = (
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.insights.hasRevivalData.queryOptions(undefined, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000,
    }),
    options,
  );

/**
 * Get revival overview KPIs with comparison to previous period
 */
export const useRevivalKPIs = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.insights.getRevivalOverviewKPIs.queryOptions(params, {
      enabled: options?.enabled ?? true,
      staleTime: 5 * 60 * 1000,
    }),
    options,
  );

/**
 * Get revival funnel data (eligible -> contacted -> responded -> recovered)
 */
export const useRevivalFunnelData = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.insights.getRevivalFunnelData.queryOptions(params, {
      enabled: options?.enabled ?? true,
      staleTime: 15 * 60 * 1000,
    }),
    options,
  );

/**
 * Get daily revival trend data
 */
export const useRevivalDailyTrend = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.insights.getRevivalDailyTrend.queryOptions(params, {
      enabled: options?.enabled ?? true,
      staleTime: 15 * 60 * 1000,
    }),
    options,
  );

/**
 * Get revival channel effectiveness data
 */
export const useRevivalChannelEffectiveness = (
  params: { dateFrom: string; dateTo: string; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.insights.getRevivalChannelEffectiveness.queryOptions(params, {
      enabled: options?.enabled ?? true,
      staleTime: 15 * 60 * 1000,
    }),
    options,
  );

/**
 * Get revival stage performance data
 */
export const useRevivalStagePerformance = (
  params: { dateFrom: string; dateTo: string; channel?: ChannelFilterType; workflowId?: string },
  options?: { enabled?: boolean } & WrappedQueryOptions,
) =>
  useWrappedQuery(
    trpc.zubeAgents.insights.getRevivalStagePerformance.queryOptions(params, {
      enabled: options?.enabled ?? true,
      staleTime: 15 * 60 * 1000,
    }),
    options,
  );
